"use client";

import Image from "next/image";
import { useMemo, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Link } from "@/i18n/navigation";
import { Button } from "@/components/ui/Button";
import { BookingCalendar } from "@/components/booking/BookingCalendar";
import {
  quoteBooking,
  isWithinLastMinuteWindow,
  nightlyPrices,
  type PaymentMethod,
  type PricingSettings,
} from "@/lib/pricing";
import { formatAmd } from "@/lib/config";

export type StayOption = {
  slug: string;
  name: string;
  pricePerNight: number;
  maxGuests: number;
  petsAllowed: boolean;
};

export type PaymentAvailability = {
  card: boolean;
  idram: boolean;
  cash: boolean;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

// Enumerate the night keys (YYYY-MM-DD) a stay occupies: check-in inclusive,
// check-out exclusive (the departure day is free).
function nightsBetween(checkIn: string, checkOut: string): string[] {
  const out: string[] = [];
  const d = new Date(`${checkIn}T00:00:00.000Z`);
  const end = new Date(`${checkOut}T00:00:00.000Z`);
  while (d < end) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

export function BookingForm({
  stays,
  initialSlug,
  lockedStay = false,
  settings,
  payments,
  localGuest = true,
  unavailable = {},
  prices = {},
}: {
  stays: StayOption[];
  initialSlug?: string;
  /** The guest arrived from a specific stay — show it as text, not a picker. */
  lockedStay?: boolean;
  settings: PricingSettings;
  payments: PaymentAvailability;
  /** Armenian phone number: cash on arrival is offered only to local guests.
   *  Everyone else prepays in full and the booking is non-refundable. */
  localGuest?: boolean;
  /** YYYY-MM-DD unavailable nights per stay slug. */
  unavailable?: Record<string, string[]>;
  /** Per-day price overrides per stay slug ({ slug: { "YYYY-MM-DD": price } }). */
  prices?: Record<string, Record<string, number>>;
}) {
  // Payment methods currently offered.
  const methods: PaymentMethod[] = [
    ...(payments.card ? (["CARD"] as const) : []),
    ...(payments.idram ? (["IDRAM"] as const) : []),
    // Cash on arrival: local (Armenian) guests only.
    ...(payments.cash && localGuest ? (["CASH"] as const) : []),
  ];
  const t = useTranslations("Booking");
  const tCommon = useTranslations("Common");
  const locale = useLocale();

  const [slug, setSlug] = useState(
    initialSlug && stays.some((s) => s.slug === initialSlug)
      ? initialSlug
      : stays[0]?.slug,
  );
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [hasPet, setHasPet] = useState(false);
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    methods[0] ?? "CARD",
  );
  const [lastMinuteOptIn, setLastMinuteOptIn] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const stay = stays.find((s) => s.slug === slug);
  const petsAllowed = stay?.petsAllowed ?? false;

  // Last-minute offer: shown only inside the window AND when an online method
  // is available. Opting in = discount + online-only; declining = cash deposit.
  const onlineMethods = methods.filter((m) => m !== "CASH");
  const withinWindow = (() => {
    if (!checkIn) return false;
    const ci = new Date(checkIn);
    if (isNaN(ci.getTime())) return false;
    return isWithinLastMinuteWindow(
      ci,
      new Date(),
      settings.lastMinuteWindowHours,
    );
  })();
  const showLastMinute = withinWindow && onlineMethods.length > 0;
  const effectiveLastMinute = showLastMinute && lastMinuteOptIn;
  // Opting in removes Cash and forces an online method; otherwise the guest's
  // own choice (any of the enabled methods, including Cash) is used as-is.
  const effectivePayment: PaymentMethod = effectiveLastMinute
    ? paymentMethod !== "CASH" && onlineMethods.includes(paymentMethod)
      ? paymentMethod
      : onlineMethods[0]
    : paymentMethod;

  const stayPrices = useMemo(
    () => prices[slug ?? ""] ?? {},
    [prices, slug],
  );

  const quote = useMemo(() => {
    if (!stay || !checkIn || !checkOut) return null;
    const ci = new Date(checkIn);
    const co = new Date(checkOut);
    if (isNaN(ci.getTime()) || isNaN(co.getTime()) || co <= ci) return null;
    return quoteBooking({
      pricePerNight: stay.pricePerNight,
      checkIn: ci,
      checkOut: co,
      paymentMethod: effectivePayment,
      settings,
      lastMinute: effectiveLastMinute,
      nightlyPrices: nightlyPrices(ci, co, stay.pricePerNight, stayPrices),
    });
  }, [
    stay,
    checkIn,
    checkOut,
    effectivePayment,
    effectiveLastMinute,
    settings,
    stayPrices,
  ]);

  // The "Rate" line: a single value only when every relevant night costs the
  // same. With per-day price differences it shows "—" (the subtotal is the sum).
  const rateDisplay = useMemo(() => {
    const dash = "—";
    if (!stay) return dash;
    if (checkIn && checkOut) {
      const ci = new Date(checkIn);
      const co = new Date(checkOut);
      if (isNaN(ci.getTime()) || isNaN(co.getTime()) || co <= ci) return dash;
      const np = nightlyPrices(ci, co, stay.pricePerNight, stayPrices);
      const uniq = new Set(np);
      return uniq.size === 1
        ? `${formatAmd([...uniq][0])} ${tCommon("currency")}`
        : dash;
    }
    // Before dates are picked: "—" if any override differs from the default.
    const varies = Object.values(stayPrices).some(
      (p) => p !== stay.pricePerNight,
    );
    return varies
      ? dash
      : `${formatAmd(stay.pricePerNight)} ${tCommon("currency")}`;
  }, [stay, checkIn, checkOut, stayPrices, tCommon]);

  // Does the chosen range overlap any unavailable night for this stay?
  const unavailableSet = useMemo(
    () => new Set(unavailable[slug ?? ""] ?? []),
    [unavailable, slug],
  );
  const dateConflict = useMemo(() => {
    if (!checkIn || !checkOut) return false;
    const co = new Date(checkOut);
    const ci = new Date(checkIn);
    if (isNaN(ci.getTime()) || isNaN(co.getTime()) || co <= ci) return false;
    return nightsBetween(checkIn, checkOut).some((d) => unavailableSet.has(d));
  }, [checkIn, checkOut, unavailableSet]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!quote || quote.nights < 1) {
      setError(t("errorDates"));
      return;
    }
    if (dateConflict) {
      setError(t("errorUnavailable"));
      return;
    }
    if (adults + children < 1) {
      setError(t("errorGuests"));
      return;
    }

    setLoading(true);
    const res = await fetch("/api/bookings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        propertySlug: slug,
        checkIn,
        checkOut,
        adults,
        children,
        hasPet: petsAllowed && hasPet,
        notes,
        paymentMethod: effectivePayment,
        lastMinute: effectiveLastMinute,
        locale,
      }),
    });
    setLoading(false);

    if (res.status === 401) {
      setError(t("errorLogin"));
      return;
    }
    if (res.status === 409) {
      setError(t("errorUnavailable"));
      return;
    }
    if (!res.ok) {
      setError(t("errorDates"));
      return;
    }
    setSuccess(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (success) {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-forest-200 bg-forest-50/70 p-8 text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-forest-500 text-2xl text-white">
          ✓
        </div>
        <h2 className="mt-4 font-display text-2xl font-bold text-bark">
          {t("successTitle")}
        </h2>
        <p className="mt-2 text-stone">{t("successText")}</p>
        <Link
          href="/account"
          className="mt-6 inline-flex rounded-full bg-forest-500 px-6 py-3 text-sm font-medium text-white hover:bg-forest-600"
        >
          {t("viewMyBookings")}
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-8 lg:grid-cols-5">
      {/* Form fields */}
      <div className="space-y-5 lg:col-span-3">
        <Labeled label={t("selectStay")}>
          {lockedStay && stay ? (
            // Arrived from this stay's page — the choice is already made.
            <p className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-2.5 font-medium text-bark">
              {stay.name} — {formatAmd(stay.pricePerNight)} {tCommon("currency")}
            </p>
          ) : (
            <select
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-2.5 text-bark focus:border-forest-400 focus:bg-cream"
            >
              {stays.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name} — {formatAmd(s.pricePerNight)} {tCommon("currency")}
                </option>
              ))}
            </select>
          )}
        </Labeled>

        <BookingCalendar
          locale={locale}
          unavailable={unavailable[slug ?? ""] ?? []}
          prices={stayPrices}
          defaultPrice={stay?.pricePerNight ?? 0}
          checkIn={checkIn}
          checkOut={checkOut}
          onChange={(ci, co) => {
            setCheckIn(ci);
            setCheckOut(co);
          }}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <Labeled label={t("checkIn")}>
            <input
              type="date"
              value={checkIn}
              min={todayISO()}
              onChange={(e) => setCheckIn(e.target.value)}
              required
              className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-2.5 text-bark focus:border-forest-400 focus:bg-cream"
            />
          </Labeled>
          <Labeled label={t("checkOut")}>
            <input
              type="date"
              value={checkOut}
              min={checkIn || todayISO()}
              onChange={(e) => setCheckOut(e.target.value)}
              required
              className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-2.5 text-bark focus:border-forest-400 focus:bg-cream"
            />
          </Labeled>
        </div>

        {dateConflict && (
          <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
            {t("errorUnavailable")}
          </p>
        )}

        <div className="grid gap-5 sm:grid-cols-2">
          <Labeled label={t("guestsAdults")}>
            <NumberInput value={adults} min={1} max={stay?.maxGuests ?? 10} onChange={setAdults} />
          </Labeled>
          <Labeled label={t("guestsChildren")}>
            <NumberInput value={children} min={0} max={stay?.maxGuests ?? 10} onChange={setChildren} />
          </Labeled>
        </div>

        {petsAllowed && (
          <label className="flex items-center gap-3 rounded-xl border border-sand bg-cream/50 px-4 py-3">
            <input
              type="checkbox"
              checked={hasPet}
              onChange={(e) => setHasPet(e.target.checked)}
              className="h-5 w-5 accent-forest-500"
            />
            <span className="text-sm text-bark">🐾 {t("hasPet")}</span>
          </label>
        )}

        <Labeled label={t("notes")}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder={t("notesPlaceholder")}
            className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-2.5 text-bark focus:border-forest-400 focus:bg-cream"
          />
        </Labeled>

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-bark">
            {t("paymentMethod")}
          </legend>

          {showLastMinute && (
            <label className="mb-3 flex items-start gap-3 rounded-xl border border-clay/40 bg-clay/5 px-4 py-3">
              <input
                type="checkbox"
                checked={lastMinuteOptIn}
                onChange={(e) => setLastMinuteOptIn(e.target.checked)}
                className="mt-0.5 h-5 w-5 accent-forest-500"
              />
              <span className="text-sm leading-relaxed text-bark">
                <span className="font-medium text-clay-dark">
                  {t("lastMinuteOptInTitle", {
                    percent: settings.lastMinuteDiscountPercent,
                  })}
                </span>
                <br />
                <span className="text-stone">{t("lastMinuteOptInText")}</span>
              </span>
            </label>
          )}

          {/* Guests outside Armenia prepay in full, with no refund — spelled
              out before they pick a method. */}
          {!localGuest && (
            // mb-4: the fieldset has no vertical rhythm of its own, so each
            // block spaces itself off the payment cards below (the last-minute
            // banner above does the same with mb-3).
            <div className="mb-4 rounded-xl border border-clay/40 bg-clay/10 p-4">
              <p className="text-sm font-semibold text-clay-dark">
                {t("foreignPrepayTitle")}
              </p>
              <p className="mt-1 text-sm text-bark">{t("foreignPrepayText")}</p>
            </div>
          )}

          {/* Opted-in last-minute removes Cash; otherwise all enabled methods. */}
          <div className="grid gap-3 sm:grid-cols-3">
            {(effectiveLastMinute ? onlineMethods : methods).map((m) => (
              <PaymentOption
                key={m}
                value={m}
                current={effectivePayment}
                onChange={setPaymentMethod}
                label={t(
                  m === "CARD"
                    ? "paymentCard"
                    : m === "IDRAM"
                      ? "paymentIdram"
                      : "paymentCash",
                )}
              />
            ))}
          </div>
        </fieldset>
      </div>

      {/* Summary */}
      <aside className="lg:col-span-2">
        <div className="sticky top-24 space-y-4 rounded-2xl border border-sand bg-cream p-6 shadow-sm">
          <h3 className="font-display text-xl font-bold text-bark">
            {t("summaryTitle")}
          </h3>

          {quote && quote.isLastMinute && (
            <p className="rounded-full bg-clay/15 px-3 py-1 text-center text-sm font-medium text-clay-dark">
              {t("lastMinuteBadge")}
            </p>
          )}

          <dl className="space-y-2 text-sm">
            <Row label={t("nightsLabel")} value={quote ? String(quote.nights) : "—"} />
            <Row label={t("rateLabel")} value={rateDisplay} />
            <Row
              label={t("subtotal")}
              value={
                quote
                  ? `${formatAmd(quote.subtotal)} ${tCommon("currency")}`
                  : "—"
              }
            />
            {quote && quote.discountAmount > 0 && (
              <Row
                label={`${t("discountLabel")} (${quote.discountPercent}%)`}
                value={`− ${formatAmd(quote.discountAmount)} ${tCommon("currency")}`}
                accent
              />
            )}
            {quote && quote.depositAmount > 0 && (
              <Row
                label={t("depositLabel")}
                value={`${formatAmd(quote.depositAmount)} ${tCommon("currency")}`}
              />
            )}
          </dl>

          <div className="border-t border-sand pt-3">
            <Row
              label={t("totalLabel")}
              value={
                quote
                  ? `${formatAmd(quote.total)} ${tCommon("currency")}`
                  : "—"
              }
              bold
            />
            <Row
              label={t("payNowLabel")}
              value={
                quote
                  ? `${formatAmd(quote.amountDueNow)} ${tCommon("currency")}`
                  : "—"
              }
              bold
              accent
            />
          </div>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading || dateConflict}
            size="lg"
            className="w-full"
          >
            {loading ? t("submitRequest") + "…" : t("submitRequest")}
          </Button>

          <div className="space-y-2 border-t border-sand pt-3 text-xs leading-relaxed text-stone">
            <p className="font-medium text-bark">{t("rulesTitle")}</p>
            <p>{t("ruleCash", { amount: formatAmd(settings.cashDepositAmd) })}</p>
            <p>{t("ruleLastMinute")}</p>
            <p>{t("ruleApproval")}</p>
          </div>
        </div>
      </aside>
    </form>
  );
}

function Labeled({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-bark">{label}</span>
      {children}
    </label>
  );
}

function NumberInput({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(Math.max(min, Number(e.target.value) || min))}
      className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-2.5 text-bark focus:border-forest-400 focus:bg-cream"
    />
  );
}

function PaymentOption({
  value,
  current,
  onChange,
  label,
}: {
  value: PaymentMethod;
  current: PaymentMethod;
  onChange: (v: PaymentMethod) => void;
  label: string;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onChange(value)}
      className={`flex flex-col gap-2 rounded-xl border px-3 py-3 text-left text-sm transition-colors ${
        active
          ? "border-forest-400 bg-forest-50 text-forest-700"
          : "border-sand bg-cream/50 text-bark hover:border-forest-300"
      }`}
    >
      <span className="flex h-6 items-center gap-1.5">
        <PaymentLogo method={value} />
      </span>
      <span>{label}</span>
    </button>
  );
}

function PaymentLogo({ method }: { method: PaymentMethod }) {
  if (method === "IDRAM") {
    return (
      <Image
        src="/images/idram.png"
        alt="Idram"
        width={24}
        height={24}
        className="rounded-md"
      />
    );
  }
  if (method === "CASH") {
    return (
      <span className="grid h-6 w-6 place-items-center rounded-full bg-green-600 text-xs font-bold text-white">
        $
      </span>
    );
  }
  // CARD — Visa + Mastercard marks.
  return (
    <>
      <span className="text-[13px] font-bold italic leading-none tracking-tight text-[#1A1F71]">
        VISA
      </span>
      <svg
        viewBox="0 0 32 20"
        className="h-5 w-auto"
        role="img"
        aria-label="Mastercard"
      >
        <circle cx="13" cy="10" r="9" fill="#EB001B" />
        <circle cx="19" cy="10" r="9" fill="#F79E1B" />
        <ellipse cx="16" cy="10" rx="3.2" ry="8" fill="#FF5F00" />
      </svg>
    </>
  );
}

function Row({
  label,
  value,
  bold,
  accent,
}: {
  label: string;
  value: string;
  bold?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className={`${bold ? "font-semibold" : ""} text-stone`}>{label}</dt>
      <dd
        className={`${bold ? "font-bold" : ""} ${
          accent ? "text-forest-600" : "text-bark"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
